import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const RequestPasswordReset = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const getApiUrl = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:3001/api';
    }
    return 'https://app.alpenscreen.ch/api';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError(t('requestReset.errors.emailRequired'));
      setLoading(false);
      return;
    }

    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('requestReset.errors.failed'));
      }

      setSuccess(true);
      setLoading(false);

    } catch (err) {
      console.error('Request password reset error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px 14px 44px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '15px',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const iconContainerStyle = {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8'
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          maxWidth: '480px',
          width: '100%',
          padding: '60px 40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={40} color="white" />
          </div>
          
          <h1 style={{
            fontSize: '28px',
            color: '#1e293b',
            marginBottom: '12px',
            fontWeight: '700'
          }}>
            {t('requestReset.success.title')}
          </h1>
          
          <p style={{
            fontSize: '15px',
            color: '#64748b',
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            {t('requestReset.success.message')}
          </p>

          <div style={{
            background: '#f1f5f9',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#475569',
            lineHeight: '1.6'
          }}>
            <strong>{t('requestReset.success.checkInbox')}</strong>
            <ul style={{
              margin: '8px 0 0 0',
              paddingLeft: '20px',
              textAlign: 'left'
            }}>
              <li>{t('requestReset.success.inbox')}</li>
              <li>{t('requestReset.success.spam')}</li>
            </ul>
          </div>

          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
              color: 'white',
              padding: '14px 32px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(43, 127, 215, 0.3)'
            }}
          >
            {t('requestReset.success.backButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        maxWidth: '480px',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* BETA Badge */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'linear-gradient(135deg, #bae6fd 0%, #7dd3fc 100%)',
          color: '#0c4a6e',
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '700',
          letterSpacing: '1px',
          border: '2px solid #0ea5e9',
          boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)'
        }}>
          {t('requestReset.beta')}
        </div>

        <div style={{ padding: '50px 40px 30px' }}>
          {/* Logo - Clickable */}
          <div 
            onClick={() => navigate('/')}
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '24px'
            }}
          >
            <img 
              src="/assets/logo/alpenscreen-horizontal-transparent.svg" 
              alt="AlpenScreen" 
              style={{
                height: '55px',
                width: 'auto'
              }}
              onError={(e) => {
                e.target.src = '/assets/logo/alpenscreen-horizontal-512px.png';
              }}
            />
          </div>

          {/* Swiss Badge */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              padding: '8px 16px',
              borderRadius: '50px',
              fontSize: '13px',
              fontWeight: '600',
              border: '1px solid #93c5fd',
              color: '#1e40af'
            }}>
              <svg 
                viewBox="0 0 32 32" 
                width="18" 
                height="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="32" height="32" fill="#DA291C"/>
                <rect x="13" y="6" width="6" height="20" fill="white"/>
                <rect x="6" y="13" width="20" height="6" fill="white"/>
              </svg>
              <span>{t('requestReset.swissMade')}</span>
            </div>
          </div>

          <h1 style={{
            fontSize: '28px',
            color: '#1e293b',
            marginBottom: '12px',
            textAlign: 'center',
            fontWeight: '700'
          }}>
            {t('requestReset.title')}
          </h1>
          
          <p style={{
            fontSize: '15px',
            color: '#64748b',
            textAlign: 'center',
            marginBottom: '0',
            lineHeight: '1.5'
          }}>
            {t('requestReset.subtitle')}
          </p>
        </div>

        {/* Blue Separator Bar */}
        <div style={{
          height: '4px',
          background: 'linear-gradient(90deg, #2B7FD7 0%, #5CA3EC 100%)',
          boxShadow: '0 2px 8px rgba(43, 127, 215, 0.2)'
        }}></div>

        <div style={{ padding: '40px 40px 50px' }}>

          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '14px',
              borderRadius: '12px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#334155',
                marginBottom: '10px'
              }}>
                {t('requestReset.emailLabel')}
              </label>
              <div style={{ position: 'relative' }}>
                <div style={iconContainerStyle}>
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={inputStyle}
                  placeholder={t('requestReset.emailPlaceholder')}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2B7FD7';
                    e.target.style.boxShadow = '0 0 0 4px rgba(43, 127, 215, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
                color: 'white',
                padding: '16px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(43, 127, 215, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(43, 127, 215, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(43, 127, 215, 0.3)';
                }
              }}
            >
              {loading ? t('requestReset.submitting') : t('requestReset.submitButton')}
            </button>
          </form>

          <button
            onClick={() => navigate('/login')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              background: 'none',
              border: 'none',
              color: '#64748b',
              padding: '12px',
              marginTop: '16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#2B7FD7';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#64748b';
            }}
          >
            <ArrowLeft size={16} />
            {t('requestReset.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestPasswordReset;
